import { useMemo } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { EngagementFieldAvailability } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { useAdminPageFilters } from '@/hooks/useAdminPageFilters';
import {
  DEFAULT_ENGAGEMENT_TIMEZONE,
  HEATMAP_TIMEZONES,
} from '@/lib/heatmapTimezones';
import {
  boundsForTimeRange,
  daysForTimeRange,
  labelForTimeRange,
  shouldShowPriorComparison,
} from '@/lib/timeRangeOptions';
import { useEngagementDashboard, useEngagementHeatmap } from '../api';
import { EngagementFilters } from '../components/EngagementFilters';
import { EngagementHeatmapCard } from '../components/EngagementHeatmapCard';
import { EngagementKpiCards } from '../components/EngagementKpiCards';
import { ReferralsCard } from '../components/ReferralsCard';
import { CommunityEngagementCard, MonetisationEngagementCard } from '../components/TimeseriesCards';
import { TopPaidPostsCard, TopPerformingContentCard } from '../components/TopListsCard';
import { TotalContentCard } from '../components/TotalContentCard';
import {
  buildEngagementDashboardCsv,
  downloadTextFile,
  engagementCsvFilename,
} from '../lib/exportEngagementCsv';
import { hourCountsInTimezone } from '../lib/contentHourBuckets';

function num(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : Number(value);
}

/**
 * Audience-only rate for Engagement by Content Type. Tips stay out: they are
 * filtered by the creator's Stripe country, unlike views, likes, comments and shares.
 */
function audienceEngagementRate(
  views: number,
  likes: number,
  comments: number,
  shares: number | null,
): number | null {
  if (views <= 0) return null;
  const rate = ((likes + comments + (shares ?? 0)) * 100) / views;
  return Math.round(rate * 100) / 100;
}

function availability(
  value: string | undefined,
): (typeof EngagementFieldAvailability)[keyof typeof EngagementFieldAvailability] {
  if (value === EngagementFieldAvailability.INCOMPLETE) {
    return EngagementFieldAvailability.INCOMPLETE;
  }
  if (value === EngagementFieldAvailability.COMING_SOON) {
    return EngagementFieldAvailability.COMING_SOON;
  }
  return EngagementFieldAvailability.AVAILABLE;
}

function rangeLabelFor(range: string): string {
  return labelForTimeRange(range);
}

/** "May 13 - May 19" for the window immediately before the selected one. */
function priorWindowLabel(startDate: string | undefined, days: number | null): string | undefined {
  if (!startDate || days == null) return undefined;
  try {
    const start = parseISO(startDate);
    return `${format(subDays(start, days), 'MMM d')} - ${format(subDays(start, 1), 'MMM d')}`;
  } catch {
    return undefined;
  }
}

/**
 * Engagement Dashboard (TM-3401). Date, audience location and timezone are one
 * page-level filter set synced to `?range=&country=&timezone=` for shareable
 * deep links. Chart cards show those pickers; changing any updates every module.
 */
export function EngagementPage() {
  const { range, setRange, countryCode, setCountryCode, timezone, setTimezone } =
    useAdminPageFilters({
      defaultTimezone: DEFAULT_ENGAGEMENT_TIMEZONE,
      allowedTimezones: HEATMAP_TIMEZONES.map((zone) => zone.value),
    });

  const globalDays = daysForTimeRange(range);
  const bounds = boundsForTimeRange(range);
  const country = countryCode || undefined;
  const showPriorComparison = shouldShowPriorComparison(range);

  const overviewQuery = useEngagementDashboard(bounds, country);
  const heatmapQuery = useEngagementHeatmap(bounds, country, timezone);

  const overviewBusy =
    !overviewQuery.isError && (overviewQuery.isPending || overviewQuery.isPlaceholderData);

  const data = overviewQuery.data;
  const comparisonLabel = showPriorComparison
    ? priorWindowLabel(
        data?.startDate == null ? undefined : String(data.startDate),
        globalDays,
      )
    : undefined;

  const kpis = useMemo(
    () =>
      (data?.kpis ?? []).map((kpi) => ({
        ...(kpi.key != null ? { key: kpi.key } : {}),
        label: kpi.label ?? '—',
        value: kpi.value == null ? null : num(kpi.value),
        delta:
          !showPriorComparison || kpi.deltaPercent == null ? null : num(kpi.deltaPercent),
        ...(kpi.description != null ? { description: kpi.description } : {}),
        ...(kpi.availability != null ? { availability: availability(kpi.availability) } : {}),
        ...(kpi.note != null ? { note: kpi.note } : {}),
      })),
    [data?.kpis, showPriorComparison],
  );

  const monetisationKpis = useMemo(
    () =>
      (data?.monetisationKpis ?? []).map((kpi) => ({
        ...(kpi.key != null ? { key: kpi.key } : {}),
        label: kpi.label ?? '—',
        value: kpi.value == null ? null : num(kpi.value),
        delta:
          !showPriorComparison || kpi.deltaPercent == null ? null : num(kpi.deltaPercent),
        ...(kpi.description != null ? { description: kpi.description } : {}),
        ...(kpi.availability != null ? { availability: availability(kpi.availability) } : {}),
      })),
    [data?.monetisationKpis, showPriorComparison],
  );

  const contentSlices = useMemo(
    () =>
      (data?.contentByType ?? []).map((row) => ({
        type: row.type ?? '',
        label: row.label ?? row.type ?? '—',
        count: num(row.count),
        percentage: num(row.percentage),
        delta:
          !showPriorComparison || row.deltaPercent == null ? null : num(row.deltaPercent),
        hourCounts: hourCountsInTimezone(
          (row.hourCounts ?? []).map(num),
          timezone,
          data?.startDate == null ? undefined : String(data.startDate),
          data?.endDate == null ? undefined : String(data.endDate),
        ),
      })),
    [data?.contentByType, data?.endDate, data?.startDate, showPriorComparison, timezone],
  );

  const engagementRows = useMemo(
    () =>
      (data?.engagementByContentType ?? []).map((row) => ({
        type: row.type ?? '',
        label: row.label ?? row.type ?? '—',
        views: num(row.views),
        likes: num(row.likes),
        comments: num(row.comments),
        shares: row.shares == null ? null : num(row.shares),
        ...(row.sharesAvailability != null
          ? { sharesAvailability: availability(row.sharesAvailability) }
          : {}),
        engagementRate: audienceEngagementRate(
          num(row.views),
          num(row.likes),
          num(row.comments),
          row.shares == null ? null : num(row.shares),
        ),
        hourCounts: hourCountsInTimezone(
          (row.hourCounts ?? []).map(num),
          timezone,
          data?.startDate == null ? undefined : String(data.startDate),
          data?.endDate == null ? undefined : String(data.endDate),
        ),
      })),
    [data?.engagementByContentType, data?.endDate, data?.startDate, timezone],
  );

  const monetisationTimeseries = useMemo(() => {
    const source = data?.monetisationTimeseries;
    return {
      ...(source?.availability != null ? { availability: availability(source.availability) } : {}),
      ...(source?.note != null ? { note: source.note } : {}),
      periods: source?.periods ?? [],
      series: (source?.series ?? []).map((series) => ({
        name: series.name ?? 'Series',
        ...(series.category != null ? { category: series.category } : {}),
        total: num(series.total),
        values: (series.values ?? []).map(num),
      })),
    };
  }, [data?.monetisationTimeseries]);

  const communityTimeseries = useMemo(() => {
    const source = data?.communityTimeseries;
    return {
      ...(source?.availability != null ? { availability: availability(source.availability) } : {}),
      ...(source?.note != null ? { note: source.note } : {}),
      periods: source?.periods ?? [],
      series: (source?.series ?? []).map((series) => ({
        name: series.name ?? 'Series',
        ...(series.category != null ? { category: series.category } : {}),
        total: num(series.total),
        values: (series.values ?? []).map(num),
      })),
    };
  }, [data?.communityTimeseries]);

  const referrals = useMemo(
    () => ({
      ...(data?.referrals?.availability != null
        ? { availability: availability(data.referrals.availability) }
        : {}),
      ...(data?.referrals?.note != null ? { note: data.referrals.note } : {}),
      successfulReferrals:
        data?.referrals?.successfulReferrals == null
          ? null
          : num(data.referrals.successfulReferrals),
      invitations: data?.referrals?.invitations == null ? null : num(data.referrals.invitations),
      ...(data?.referrals?.invitationsAvailability != null
        ? { invitationsAvailability: availability(data.referrals.invitationsAvailability) }
        : {}),
      conversionRate:
        data?.referrals?.conversionRate == null ? null : num(data.referrals.conversionRate),
      ...(data?.referrals?.conversionRateAvailability != null
        ? { conversionRateAvailability: availability(data.referrals.conversionRateAvailability) }
        : {}),
    }),
    [data?.referrals],
  );

  const topPaid = useMemo(
    () =>
      (data?.topPaidPosts ?? []).map((item) => ({
        postId: num(item.postId),
        title: item.title ?? '(untitled)',
        ...(item.contentType != null ? { contentType: item.contentType } : {}),
        ...(item.contentTypeLabel != null ? { contentTypeLabel: item.contentTypeLabel } : {}),
        ...(item.creatorDisplayName != null ? { creatorDisplayName: item.creatorDisplayName } : {}),
        creatorAvatarUrl: item.creatorAvatarUrl ?? null,
        thumbnailUrl: item.thumbnailUrl ?? null,
        purchaseCount: item.purchaseCount == null ? null : num(item.purchaseCount),
      })),
    [data?.topPaidPosts],
  );

  const topPerforming = useMemo(
    () =>
      (data?.topPerformingContent ?? []).map((item) => ({
        postId: num(item.postId),
        title: item.title ?? '(untitled)',
        ...(item.contentType != null ? { contentType: item.contentType } : {}),
        ...(item.contentTypeLabel != null ? { contentTypeLabel: item.contentTypeLabel } : {}),
        ...(item.creatorDisplayName != null ? { creatorDisplayName: item.creatorDisplayName } : {}),
        creatorAvatarUrl: item.creatorAvatarUrl ?? null,
        thumbnailUrl: item.thumbnailUrl ?? null,
        views: item.views == null ? null : num(item.views),
      })),
    [data?.topPerformingContent],
  );

  function handleExport() {
    if (!data) {
      toast.error('Nothing to export yet.');
      return;
    }
    const csv = buildEngagementDashboardCsv({
      rangeLabel: rangeLabelFor(range),
      days: globalDays,
      countryCode,
      timezone,
      ...(data.startDate != null ? { startDate: String(data.startDate) } : {}),
      ...(data.endDate != null ? { endDate: String(data.endDate) } : {}),
      kpis: kpis.map((kpi) => ({ label: kpi.label, value: kpi.value, delta: kpi.delta })),
      monetisationKpis: monetisationKpis.map((kpi) => ({
        label: kpi.label,
        value: kpi.value,
        delta: kpi.delta,
      })),
      contentByType: contentSlices.map((row) => ({
        label: row.label,
        count: row.count,
        percentage: row.percentage,
        hourCounts: row.hourCounts,
      })),
      engagementByType: engagementRows.map((row) => ({
        label: row.label,
        views: row.views,
        likes: row.likes,
        comments: row.comments,
        shares: row.shares,
        engagementRate: row.engagementRate,
        hourCounts: row.hourCounts,
      })),
      monetisationPeriods: monetisationTimeseries.periods,
      monetisationSeries: monetisationTimeseries.series.map((series) => ({
        name: series.name,
        values: series.values,
      })),
      communityPeriods: communityTimeseries.periods,
      communitySeries: communityTimeseries.series.map((series) => ({
        name: series.name,
        values: series.values,
      })),
      referrals: {
        successful: referrals.successfulReferrals,
        invitations: referrals.invitations,
        conversionRate: referrals.conversionRate,
      },
      topPaid: topPaid.map((item) => ({
        title: item.title,
        creator: item.creatorDisplayName ?? '',
        purchases: item.purchaseCount ?? 0,
      })),
      topPerforming: topPerforming.map((item) => ({
        title: item.title,
        type: item.contentTypeLabel ?? '',
        creator: item.creatorDisplayName ?? '',
        views: item.views ?? 0,
      })),
    });
    downloadTextFile(engagementCsvFilename(globalDays), csv);
    toast.success('Engagement CSV downloaded.');
  }

  if (overviewQuery.isLoading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (overviewQuery.isError && !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <AlertCircle className="size-8 text-rose-400" />
        <p className="text-sm text-muted-foreground">Failed to load engagement dashboard.</p>
        <Button type="button" variant="outline" onClick={() => void overviewQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const startDate = data?.startDate == null ? undefined : String(data.startDate);
  const endDate = data?.endDate == null ? undefined : String(data.endDate);
  const availableCountryCodes = data?.availableCountryCodes ?? [];

  return (
    <div className="space-y-4 p-4 md:p-6">
      <EngagementFilters
        range={range}
        onRangeChange={setRange}
        countryCode={countryCode}
        onCountryCodeChange={setCountryCode}
        availableCountryCodes={availableCountryCodes}
        {...(!overviewBusy && startDate ? { startDate } : {})}
        {...(!overviewBusy && endDate ? { endDate } : {})}
        onExportCsv={handleExport}
        exportDisabled={!data}
        advancedExportComingSoon={
          data?.advancedExportAvailability === EngagementFieldAvailability.COMING_SOON
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2.15fr)_minmax(310px,1fr)]">
        <div className="space-y-4">
          <EngagementKpiCards
            metrics={kpis}
            {...(!overviewBusy && comparisonLabel ? { comparisonLabel } : {})}
            className="sm:grid-cols-2"
            isLoading={overviewBusy}
          />

          <TotalContentCard
            slices={contentSlices}
            engagementRows={engagementRows}
            range={range}
            onRangeChange={setRange}
            {...(!overviewBusy && startDate ? { startDate } : {})}
            {...(!overviewBusy && endDate ? { endDate } : {})}
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            availableCountryCodes={availableCountryCodes}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            isLoading={overviewBusy}
          />

          <EngagementKpiCards
            metrics={monetisationKpis}
            {...(!overviewBusy && comparisonLabel ? { comparisonLabel } : {})}
            className="sm:grid-cols-3"
            isLoading={overviewBusy}
          />

          <MonetisationEngagementCard
            data={monetisationTimeseries}
            range={range}
            onRangeChange={setRange}
            {...(!overviewBusy && startDate ? { startDate } : {})}
            {...(!overviewBusy && endDate ? { endDate } : {})}
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            availableCountryCodes={availableCountryCodes}
            isLoading={overviewBusy}
          />

          <CommunityEngagementCard
            data={communityTimeseries}
            range={range}
            onRangeChange={setRange}
            {...(!overviewBusy && startDate ? { startDate } : {})}
            {...(!overviewBusy && endDate ? { endDate } : {})}
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            availableCountryCodes={availableCountryCodes}
            isLoading={overviewBusy}
          />

          <EngagementHeatmapCard
            {...(heatmapQuery.isError
              ? { availability: EngagementFieldAvailability.COMING_SOON }
              : heatmapQuery.data?.availability != null
                ? { availability: availability(heatmapQuery.data.availability) }
                : {})}
            {...(heatmapQuery.isError
              ? { note: 'Failed to load heatmap.' }
              : heatmapQuery.data?.note != null
                ? { note: heatmapQuery.data.note }
                : {})}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            {...(heatmapQuery.data?.timezone === timezone && heatmapQuery.data.cells != null
              ? { cells: heatmapQuery.data.cells }
              : {})}
            isLoading={
              !heatmapQuery.isError &&
              (heatmapQuery.isPending ||
                heatmapQuery.isPlaceholderData ||
                heatmapQuery.data?.timezone !== timezone)
            }
          />
        </div>

        <div className="space-y-4">
          <ReferralsCard data={referrals} isLoading={overviewBusy} />
          <TopPaidPostsCard items={topPaid} isLoading={overviewBusy} />
          <TopPerformingContentCard items={topPerforming} isLoading={overviewBusy} />
        </div>
      </div>
    </div>
  );
}
